<template>
    <Dialog v-model:visible="visibleLocal" modal :header="t('billing.buy_assessments.dialog_title')"
        :style="{ width: '520px' }" :closable="true">
        <div class="flex flex-col gap-4">
            <InlineMessage severity="info">{{ t('billing.buy_assessments.description') }}</InlineMessage>

            <div v-if="step === 1" class="flex flex-col gap-4">
                <div class="flex flex-col gap-2">
                    <label class="text-sm text-gray-700">{{ t('billing.buy_assessments.quantity_label') }}</label>
                    <InputNumber v-model="quantity" :min="1" :max="999" :useGrouping="false" inputClass="w-full"
                        showButtons buttonLayout="horizontal" :step="1" decrementButtonClass="p-button-text"
                        incrementButtonClass="p-button-text" incrementButtonIcon="pi pi-plus"
                        decrementButtonIcon="pi pi-minus" />
                </div>

                <div class="flex flex-col gap-1 text-sm text-gray-600" v-if="singleAssessmentPrice">
                    <div class="flex items-center justify-between"><span>{{ t('billing.buy_assessments.price_each')
                    }}</span>
                        <span class="text-gray-800">{{ formatEuro(singleAssessmentPrice.unit_amount) }}{{
                            currencySymbol }}</span>
                    </div>
                    <div class="flex items-center justify-between font-medium"><span>{{
                        t('billing.buy_assessments.total')
                            }}</span>
                        <span>{{ formatEuro((singleAssessmentPrice.unit_amount || 0) * quantity) }}{{ currencySymbol
                            }}</span>
                    </div>
                </div>

                <InlineMessage v-if="!isLoadingPricing && !singleAssessmentPrice" severity="warn">{{
                    t('billing.buy_assessments.no_price_found') }}</InlineMessage>
            </div>

            <div v-else-if="step === 2" class="flex flex-col gap-3">
                <div class="flex flex-col items-center gap-2">
                    <div class="flex items-center gap-2 text-sm text-gray-700">
                        <ProgressSpinner style="width:20px;height:20px" strokeWidth="4" />
                        <span>{{ t('billing.buy_assessments.creating_checkout') }}</span>
                    </div>
                    <span class="text-xs text-color-secondary">{{ t('shared.loading_may_take_time') }}</span>
                </div>
                <InlineMessage v-if="errorMessage" severity="error">{{ errorMessage }}</InlineMessage>
            </div>

            <div v-else-if="step === 3" class="flex flex-col gap-3">
                <div class="flex flex-col items-center gap-2">
                    <div class="flex items-center gap-2 text-sm text-gray-700">
                        <ProgressSpinner style="width:20px;height:20px" strokeWidth="4" />
                        <span>{{ t('billing.buy_assessments.redirecting') }}</span>
                    </div>
                    <span class="text-xs text-color-secondary">{{ t('shared.loading_may_take_time') }}</span>
                </div>
            </div>
        </div>

        <template #footer>
            <div v-if="step === 1" class="flex justify-end gap-2">
                <Button :label="t('shared.cancel')" severity="secondary" text @click="onCancel" :disabled="isWorking" />
                <Button :label="t('billing.buy_assessments.buy')" :disabled="!canBuy || isWorking" @click="onBuy" />
            </div>
            <div v-else-if="step === 2" class="flex justify-end gap-2">
                <Button :label="t('shared.cancel')" severity="secondary" text :disabled="true" />
            </div>
        </template>
    </Dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, watchEffect } from 'vue'
import { useI18n } from 'vue-i18n'
import Dialog from 'primevue/dialog'
import InputNumber from 'primevue/inputnumber'
import Button from 'primevue/button'
import InlineMessage from 'primevue/inlinemessage'
import ProgressSpinner from 'primevue/progressspinner'
import { storeToRefs } from 'pinia'

import { useBillingStore } from '@/stores/billing'
import { useTenantsStore } from '@/stores/tenants'
import { PriceLookupKeys } from '@/models/Billing'
import { TenantPlan } from '@/models/Tenant'
import { logger } from '@/utils/logger'
import { useSelfServeGate } from '@/composables/useSelfServeGate'

const props = defineProps<{ visible: boolean }>()
const emit = defineEmits<{ (e: 'update:visible', value: boolean): void }>()

const { t } = useI18n()
const billingStore = useBillingStore()
const tenantsStore = useTenantsStore()
const { checkoutSession, pricingPlans, isLoadingPricingPlans } = storeToRefs(billingStore)
const { currentTenant } = storeToRefs(tenantsStore)
const { canSelfServeTopup, openContactSales } = useSelfServeGate()

const visibleLocal = ref(props.visible)
watch(() => props.visible, (v) => (visibleLocal.value = v))
watch(visibleLocal, (v) => emit('update:visible', v))

const step = ref(1)
const isWorking = ref(false)
const errorMessage = ref<string | null>(null)
const quantity = ref(1)

const isLoadingPricing = computed(() => isLoadingPricingPlans.value)

const activePlan = computed<TenantPlan | null>(() => (currentTenant.value?.plan as TenantPlan) || null)
const singleAssessmentPricing = computed(() => (
    pricingPlans.value?.getPricingByLookupKey(PriceLookupKeys.SINGLE_ASSESSMENT) || null
))
const singleAssessmentPrice = computed(() => singleAssessmentPricing.value?.price || null)

onMounted(async () => {
    // Ensure subscription is active (will skip if already subscribed)
    await tenantsStore.listTenants(false)
    await billingStore.loadActivePricingPlans()
})

watchEffect(() => {
    const url = checkoutSession.value?.url
    if (url) {
        step.value = 3
        window.location.replace(url)
    }
})

const canBuy = computed(() => quantity.value > 0 && !!singleAssessmentPrice.value && !!currentTenant.value?.id && !!activePlan.value)

function resetForm() {
    step.value = 1
    isWorking.value = false
    errorMessage.value = null
    quantity.value = 1
}

function onCancel() {
    visibleLocal.value = false
    resetForm()
}

function formatEuro(amountCents: number | null | undefined): string {
    const amount = (amountCents ?? 0) / 100
    return new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0, minimumFractionDigits: 0 }).format(amount)
}
const currencySymbol = computed(() => t('pricing.currency.eur'))

async function onBuy() {
    if (!canBuy.value) return
    // Self-serve gate (MYAC-837): catch-all for any opener of this dialog; close
    // this dialog first so Contact sales doesn't stack behind it.
    if (!canSelfServeTopup()) {
        visibleLocal.value = false
        openContactSales('topup')
        return
    }
    try {
        errorMessage.value = null
        isWorking.value = true
        step.value = 2
        const tenant = currentTenant.value
        const plan = activePlan.value
        const priceId = singleAssessmentPricing.value?.price_id
        if (!tenant?.id || !plan || !priceId) throw new Error(t('billing.buy_assessments.no_price_found'))

        const quantities: Record<string, number> = { [priceId]: quantity.value }
        await billingStore.createCheckoutSessionRequest(tenant.id, plan, quantities)
    } catch (e) {
        logger.error('Error creating checkout for assessments', e)
        errorMessage.value = (e as Error)?.message || t('billing.buy_assessments.error_generic')
        isWorking.value = false
        step.value = 1
    }
}
</script>

<style scoped></style>
